import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Input } from "../../../../components";
import AnimatedDots from "../../../../components/AnimatedDots/AnimatedDots";
import CircularIndeterminate from "../../../../components/circleLoader/CircleLoader";
import { AppRoutes, LocalStorageItems } from "../../../../constants";
import useGetUser from "../../../../hooks/useGetUser";
import { Donorlogin_V2 } from "../../../../Services_V2/Auth.service";
import { showError } from "../../../../utils/error";
import { IVerifyOtpInputs, verifyOtpSchema, VryfyOtpInputs } from "../const";
import styles from "./donor-verify-otp.module.css";

const VerifyOtpPage: React.FC = () => {
  const navigation = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const getUser = useGetUser();
  const stateEmail = (location.state as { email?: string })?.email || "";
  const storedEmail = sessionStorage.getItem("donorPendingEmail") || "";
  const email = stateEmail || storedEmail || "";

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<IVerifyOtpInputs>({
    resolver: yupResolver(verifyOtpSchema),
    defaultValues: {
      [VryfyOtpInputs.EMAIL]: email,
    },
  });

  useEffect(() => {
    if (email) {
      setValue(VryfyOtpInputs.EMAIL, email, { shouldValidate: true });
      sessionStorage.removeItem("donorPendingEmail"); // Cleanup
    } else {
      navigation(AppRoutes.DONOR.DONOR_LOGIN);
    }
  }, [email, setValue, navigation]);

  const onSubmit = useCallback(
    async (values: IVerifyOtpInputs) => {
      setLoading(true);
      try {
        const response = await Donorlogin_V2({
          email: values[VryfyOtpInputs.EMAIL],
          otp: Number(values[VryfyOtpInputs.OTP]),
        });

        if (response.success && response.data?.token) {
          localStorage.setItem(LocalStorageItems.TOKEN, response.data.token);
          await getUser();

          const returnTo = (location.state as { returnTo?: string })?.returnTo;

          if (returnTo) {
            navigation(returnTo, { replace: true });
          } else {
            navigation(AppRoutes.ORGANIZATION.MY_DONATION, { replace: true });
          }
        } else {
          showError("Login failed. Please try again.");
        }
      } catch (error: any) {
        showError(
          error?.response?.data?.message || "Invalid OTP. Please try again."
        );
      } finally {
        setLoading(false);
      }
    },
    [navigation, location.state]
  );

  return (
    <div className={styles.container}>
      <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.header}>
          <Typography variant="h5" className={styles.logo}>
            Verify OTP
          </Typography>
          <Typography variant="body2" color="text.secondary">
            We&apos;ve sent a 6-digit code to
          </Typography>
          <Typography variant="subtitle1" fontWeight="bold" mt={1}>
            {email || "your email"}
          </Typography>
        </div>

        <div className={styles.formControls}>
          {/* Hidden or read-only email */}
          <Input
            fullWidth
            label="Email"
            value={email}
            disabled
            placeholder="Your email"
            sx={{ bgcolor: "#f5f5f5" }}
          />

          <Input
            fullWidth
            label="Enter OTP"
            placeholder="6-digit code"
            {...register(VryfyOtpInputs.OTP)}
            error={!!errors[VryfyOtpInputs.OTP]}
            helperText={errors[VryfyOtpInputs.OTP]?.message}
            inputProps={{ maxLength: 6 }}
            autoFocus
          />
        </div>

        <Button
          type="submit"
          fullWidth
          sx={{ mt: 3 }}
          disabled={loading}
          startIcon={loading ? <CircularIndeterminate size={20} /> : null}
        >
          {loading ? <AnimatedDots text="Verifying OTP" /> : "Verify & login"}
        </Button>

        <Box textAlign="center" mt={2}>
          <Button
            variant="text"
            onClick={() => navigation(AppRoutes.DONOR.DONOR_LOGIN)}
          >
            Wrong email? Go back
          </Button>
        </Box>
      </form>
    </div>
  );
};

export default VerifyOtpPage;
